import { selectModeName } from "@/store/mode-selectors";
import { changeMode } from "@/store/mode-slice";
import {
  selectCompareElement,
  selectCompareShouldUseIntensityData,
  selectWalkCompareSceneFilter,
  setCompareElementId,
  setCompareSceneFilter,
  setCompareShouldUseIntensityData,
} from "@/store/modes/walk-mode-slice";
import { SceneFilter } from "@/types/scene-filter";
import { includes } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import type { SavedViewData } from "@faro-lotv/sphere-viewer-contracts";
import { elementAndMode } from "./element-and-mode";
import type { TrackedState } from "./tracked-state";

/**
 * The right side of the split mode, as a subset of a saved view. All fields are
 * absent outside of the split mode.
 */
export type SplitCompareEntry = Pick<
  SavedViewData,
  "rightId" | "rightIntensity"
> & {
  /** The scene filter of the compare view */
  rightScene?: SceneFilter;
};

/**
 * Tracks the right side of the split mode: the compare element, its scene filter
 * and intensity. The left side is the active element and mode, tracked by
 * {@link elementAndMode}.
 *
 * Not tracked here:
 * - The cameras of both sides. They are not tracked yet and will get a tracked
 *   state of their own.
 * - The camera sync toggles, on purpose: they are view settings rather than a
 *   position to go back to, so toggling them adds no history entry.
 *
 * Outside of the split mode nothing is recorded, and restoring leaves the compare
 * state alone.
 */
export const splitCompare: TrackedState<SplitCompareEntry> = {
  /**
   * @param state The current state
   * @returns the compare state in split mode, or an empty part in any other mode
   */
  capture(state) {
    const rightId = selectCompareElement(state)?.id;
    if (selectModeName(state) !== "split" || !rightId) return {};
    return {
      rightId,
      rightScene: selectWalkCompareSceneFilter(state),
      rightIntensity: selectCompareShouldUseIntensityData(state),
    };
  },

  /** @returns whether the value has no compare state, or a well formed one */
  isEntry({ rightId, rightScene, rightIntensity }) {
    if (rightId === undefined) return true;
    return (
      typeof rightId === "string" &&
      includes(Object.values(SceneFilter), rightScene) &&
      typeof rightIntensity === "boolean"
    );
  },

  /**
   * @param state The current state
   * @returns whether the compare element, if any, still exists
   */
  canRestore({ rightId }, state) {
    return !rightId || !!selectIElement(rightId)(state);
  },

  /**
   * The values are also handed to the split mode as its initial state, because
   * entering the split mode would otherwise pick the best compare element. Once in
   * the split mode the initial state is not read anymore.
   *
   * @returns a thunk restoring the compare state, if the entry has one
   */
  restore({ rightId, rightScene, rightIntensity }) {
    return (dispatch, getState) => {
      if (!rightId || !rightScene) return;
      dispatch(setCompareElementId(rightId));
      dispatch(setCompareSceneFilter(rightScene));
      dispatch(setCompareShouldUseIntensityData(!!rightIntensity));
      if (selectModeName(getState()) === "split") {
        dispatch(
          changeMode({ mode: "split", initialState: { rightId, rightScene } }),
        );
      }
    };
  },

  /** The initial state is only handed over once the mode was switched to split */
  restoreAfter: [elementAndMode],
};
